import React from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { Helmet } from "react-helmet";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTranslation } from "@/hooks/useTranslation";

const RefundPolicy = () => {
  const navigate = useNavigate();
  const { dir, isRTL } = useTranslation();

  return (
    <MainLayout>
      <Helmet>
        <title>Refund Policy | EyeControl App</title>
        <meta name="description" content="Refund Policy for EyeControl App by Eyefree Assisting Communication ltd." />
      </Helmet>

      <div className="flex-1 flex flex-col bg-background p-4 overflow-hidden" dir={dir}>
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/legal")}
            className="hover:bg-primary/10"
            data-scan-item
          >
            {isRTL ? <ArrowRight className="w-6 h-6" /> : <ArrowLeft className="w-6 h-6" />}
          </Button>
          <h1 className="text-2xl font-bold text-foreground">Refund Policy for EyeControl App</h1>
        </div>

        <ScrollArea className="flex-1 pr-4">
          <div className="prose prose-sm max-w-none text-foreground/90 space-y-6 pb-8">
            <div className="text-muted-foreground border-b border-border pb-4">
              <p><strong>Last Updated:</strong> December 10, 2025</p>
              <p><strong>Operated by:</strong> Eyefree Assisting Communication ltd.</p>
               <p className="mt-4">
41:                 At Eyefree Assisting Communication ltd., we want to ensure you are 100% happy with your purchase of EyeControl App. 
                Our policy is designed to be transparent and fair. Please read the following guidelines regarding refunds and cancellations.
              </p>
            </div>

            <section>
              <h2 className="text-xl font-semibold">1. 14-Day Money-Back Guarantee</h2>
              <p>If you are a new customer and are not satisfied with EyeControl App for any reason, you may request a full refund within 14 calendar days of your initial purchase.</p>
              <p><strong>Effect of Refund:</strong> If a refund is processed, your subscription will be terminated immediately, and you will lose access to premium features and data associated with the paid plan.</p>
              <p><strong>Eligibility:</strong> This guarantee applies to your first subscription charge only.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold">2. Cancellation</h2>
              <p>You may cancel your subscription at any time via your account settings to prevent future billing.</p>
              <p><strong>No Partial Refunds:</strong> If you cancel after the initial 14-day window, you will not receive a refund for the current billing cycle. You will continue to have access to EyeControl App until the end of your current billing period.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold">3. Annual Subscription Renewals</h2>
              <p>We understand that sometimes customers forget to cancel annual subscriptions before they renew.</p>
              <p><strong>Grace Period:</strong> If your annual plan automatically renews and you did not intend to continue, you may request a full refund within 7 days of the renewal charge date.</p>
              <p>Please note that requesting a refund for a renewal will result in the immediate downgrade of your account.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold">4. How to Request a Refund</h2>
              <p>To request a refund under these terms, please contact our support team:</p>
              <p><strong>Email:</strong> <a href="mailto:support@eyecontrol.co.il" className="text-primary hover:underline">support@eyecontrol.co.il</a></p>
              <p><strong>Subject:</strong> Refund Request - [Your Order ID]</p>
              <p>Please include your order number or the email address associated with your account so we can locate your transaction quickly. We aim to respond to all refund requests within 2-3 business days.</p>
            </section>

            <p className="text-sm text-muted-foreground mt-8">All rights reserved © Eyefree Assisting Communication ltd.</p>
          </div>
        </ScrollArea>
      </div>
    </MainLayout>
  );
};

export default RefundPolicy;
